// TestTaker.vue

<template>
  <div class="test-taker-page">
    <div class="test-card">
      <div class="timer-bar" v-if="!isLoading && stage">
        Sisa Waktu {{ stage === 'memorizing' ? 'Menghafal' : 'Mengerjakan' }}: <strong>{{ formattedTimeLeft }}</strong>
      </div>

      <!-- Tampilan Loading -->
      <div v-if="isLoading" class="loading-container">
        <div class="spinner"></div>
        <p>Mempersiapkan tes...</p>
      </div>
      
      <div v-if="error" class="error-message">{{ error }}</div>

      <!-- TAHAP HAFALAN -->
      <div v-if="!isLoading && stage === 'memorizing'" class="stage-content memorization-stage">
        <h2>Tahap Menghafal</h2>
        <p class="instruction">Anda memiliki waktu untuk menghafal informasi di bawah ini. Setelah waktu habis, sesi pertanyaan akan dimulai secara otomatis.</p>
        <div class="stimulus-content">  
          <img v-if="testDetails.stimulus_image_url" :src="`${backendUrl}${testDetails.stimulus_image_url}`" alt="Stimulus Gambar">
          <p v-if="testDetails.stimulus_text" class="stimulus-text">{{ testDetails.stimulus_text }}</p>
        </div>
      </div>

      <!-- TAHAP MENJAWAB -->
      <div v-if="!isLoading && stage === 'answering'" class="stage-content">
        <div v-if="questions.length > 0">
          
          <!-- ====================================================== -->
          <!-- PANEL NAVIGASI SOAL BARU -->
          <!-- ====================================================== -->
          <div class="question-navigator">
            <div 
              v-for="(question, index) in questions"
              :key="question.id"
              class="nav-box"
              :class="getNavBoxClass(question.id, index)"
              @click="jumpToQuestion(index)"
            >
              {{ index + 1 }}
            </div>
          </div>
          
          <!-- Progress Bar -->
          <div class="progress-bar-container">
            <div class="progress-bar-fill" :style="{ width: progressPercentage + '%' }"></div>
          </div>
          <p class="progress-text">Pertanyaan {{ currentQuestionIndex + 1 }} dari {{ questions.length }}</p>
          
          <div class="test-header">
            <div v-if="currentQuestion.image_url" class="question-image-container">
              <img :src="`https://elevaretes.com${currentQuestion.image_url}`" alt="Gambar Soal">
            </div>
            <h1 v-html="currentQuestion.text"></h1>
          </div>

          <div class="answer-area">
            <!-- Pilihan Ganda (Satu Jawaban) - Tidak Berubah -->
            <div v-if="currentQuestion.question_type === 'multiple_choice'" class="answer-options">
              <div v-for="option in currentQuestion.options" :key="option.id" class="option" @click="selectSingleAnswer(option.id)" :class="{ 'selected': selectedAnswers.includes(option.id) }">
                <img v-if="option.image_url" :src="`${backendUrl}${option.image_url}`" alt="Pilihan Jawaban" class="option-image">
                <span v-if="option.text">{{ option.text }}</span>
              </div>
            </div>

            <!-- PILIHAN GANDA (BANYAK JAWABAN) - BARU -->
            <div v-else-if="currentQuestion.question_type === 'multiple_answer'" class="answer-options">
              <label v-for="option in currentQuestion.options" :key="option.id" class="option checkbox-option" :class="{ 'selected': selectedAnswers.includes(option.id) }">
                <input type="checkbox" :value="option.id" v-model="selectedAnswers"/>
                 <img v-if="option.image_url" :src="`${backendUrl}${option.image_url}`" alt="Pilihan Jawaban" class="option-image">
                <span v-if="option.text">{{ option.text }}</span>
              </label>
            </div>
            <div v-else-if="currentQuestion.question_type === 'essay'" class="answer-essay">
              <textarea v-model="essayAnswer" placeholder="Ketik jawaban Anda di sini..." rows="8"></textarea>
            </div>
          </div>

          <div class="navigation-buttons">
            <button 
              @click="prevQuestion" 
              :disabled="isSubmitting || currentQuestionIndex === 0"
              class="button-secondary"
            >
              Sebelumnya
            </button>
            <button 
              @click="nextQuestion" 
              :disabled="isSubmitting || (!isAnswerSelected && !isLastQuestion)"
              class="button-primary"
            >
              {{ isLastQuestion ? 'Selesaikan Tes' : 'Selanjutnya' }}
            </button>
          </div>
        </div>
        
        <div v-else-if="questions.length === 0 && !error">
          <h2>Tidak Ada Pertanyaan</h2>
          <p>Tidak ada pertanyaan yang tersedia untuk tes ini.</p>
          <button @click="router.push({ name: 'TestDashboard' })">Kembali ke Dashboard</button>
        </div>
      </div>
    </div>

    <div v-if="showLeaveWarning" class="warning-overlay">
    <div class="warning-modal">
      <h2>⚠️ Peringatan</h2>
      <p>
        Aktivitas di luar jendela tes terdeteksi. Sesuai peraturan, tindakan seperti beralih aplikasi, membuka tab lain, atau mengambil tangkapan layar (screenshot) tidak diizinkan.
      </p>
      <p class="warning-count">
        Ini adalah peringatan ke-<strong>{{ leaveCount }}</strong>. Pelanggaran berulang dapat mengakibatkan sesi tes Anda dihentikan secara otomatis.
      </p>
      <button @click="showLeaveWarning = false" class="button-primary">Saya Mengerti</button>
    </div>
  </div>
  <!-- ============================================= -->
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import apiClient from '@/services/api.js';


const backendUrl = process.env.NODE_ENV === 'production' 
    ? window.location.origin 
    : 'http://148.230.97.203:8011'; // Ganti dengan IP atau localhost backend Anda
// ----------------------------

const route = useRoute();
const router = useRouter();

// --- State ---
const stage = ref('');
const testDetails = ref(null);
const isLoading = ref(true);
const questions = ref([]);
const currentQuestionIndex = ref(0);
const selectedAnswers = ref([]); // Untuk pilihan ganda (satu/banyak)
const essayAnswer = ref('');     // Untuk jawaban essay
const answers = ref({});
const error = ref('');
const timeLeft = ref(0);
const timerInterval = ref(null);
const isSubmitting = ref(false);


// --- Computed Properties (dengan pengaman) ---
// const currentQuestion = computed(() => questions.value[currentQuestionIndex.value] || {});
const currentQuestion = computed(() => {
  const q = questions.value[currentQuestionIndex.value] || {};
  console.log("CURRENT QUESTION OBJECT:", q); // <-- Tambahkan ini
  return q;
});

const isLastQuestion = computed(() => {
  // Pengaman
  if (!Array.isArray(questions.value) || questions.value.length === 0) {
    return false;
  }
  // Pastikan ada 'return' di sini
  return currentQuestionIndex.value === questions.value.length - 1;
});

const progressPercentage = computed(() => {
  if (questions.value.length === 0) {
    return 0;
  }
  // Pastikan ada 'return' di sini
  return ((currentQuestionIndex.value + 1) / questions.value.length) * 100;
});

const formattedTimeLeft = computed(() => {
  if (timeLeft.value <= 0) return '00:00:00';
  const hours = Math.floor(timeLeft.value / 3600);
  const minutes = Math.floor((timeLeft.value % 3600) / 60);
  const seconds = timeLeft.value % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
});

const isAnswerSelected = computed(() => {
  const type = currentQuestion.value.question_type;
  if (type === 'multiple_choice' || type === 'multiple_answer') {
    return selectedAnswers.value.length > 0;
  }
  if (type === 'essay') {
    return essayAnswer.value.trim() !== '';
  }
  return false;
});


// src/views/TestTaker.vue

const saveCurrentAnswer = () => {
  // Hanya simpan jika ada jawaban yang valid
  if (isAnswerSelected.value) {
    const type = currentQuestion.value.question_type;
    answers.value[currentQuestion.value.id] = {
      question_id: currentQuestion.value.id,
      selected_option_ids: (type === 'multiple_choice' || type === 'multiple_answer') ? [...selectedAnswers.value] : [], // Kirim salinan array
      answer_text: type === 'essay' ? essayAnswer.value : null
    };
  }
};

const loadAnswerForCurrentQuestion = () => {
  const currentAnswer = answers.value[currentQuestion.value.id];
  if (currentAnswer) {
    // Muat kembali jawaban yang sudah disimpan
    selectedAnswers.value = currentAnswer.selected_option_ids || [];
    essayAnswer.value = currentAnswer.answer_text || '';
  } else {
    // Reset state jika belum ada jawaban untuk soal ini
    selectedAnswers.value = [];
    essayAnswer.value = '';
  }
};

const startMemorizationTimer = () => {
  const duration = testDetails.value.memorization_duration_seconds;
  timeLeft.value = duration;

  timerInterval.value = setInterval(() => {
    timeLeft.value--;
    if (timeLeft.value <= 0) {
      clearInterval(timerInterval.value);
      stage.value = 'answering';
      startAnsweringTimer();
    }
  }, 1000);
};

const jumpToQuestion = (index) => {
  // Simpan jawaban saat ini sebelum pindah
  saveCurrentAnswer();
  currentQuestionIndex.value = index;
  // Muat ulang jawaban jika sudah ada
  loadAnswerForCurrentQuestion();
};

const startAnsweringTimer = () => {
  const storedEndTime = localStorage.getItem('test_end_time');
  if (!storedEndTime) {
    error.value = "Tidak bisa memulai timer. Sesi tidak valid.";
    return;
  }
  const endTime = new Date(storedEndTime);
  timerInterval.value = setInterval(() => {
    const now = new Date();
    const distance = endTime.getTime() - now.getTime();
    if (distance < 0) {
      clearInterval(timerInterval.value);
      timeLeft.value = 0;
      if (questions.value.length > 0) {
        alert("Waktu Habis! Jawaban Anda akan dikirim secara otomatis.");
        submitTest();
      }
    } else {
      timeLeft.value = Math.floor(distance / 1000);
    }
  }, 1000);
};

const fetchQuestions = async (testId) => {
  try {
    const response = await apiClient.get(`/tests/${testId}/questions`);
    questions.value = response.data;
  } catch (err) {
    error.value = 'Gagal memuat pertanyaan. Sesi Anda mungkin tidak valid.';
    console.error(err);
  }
};

const selectSingleAnswer = (optionId) => {
  // Set selectedAnswers menjadi array yang hanya berisi ID yang diklik
  selectedAnswers.value = [optionId];
};

const getNavBoxClass = (questionId, index) => {
  const classes = [];
  // Cek apakah soal sudah dijawab
  if (answers.value[questionId]) {
    classes.push('answered');
  }
  // Cek apakah ini soal yang sedang aktif
  if (index === currentQuestionIndex.value) {
    classes.push('active');
  }
  return classes.join(' '); // e.g., "answered active" atau "answered" atau "active"
};

const prevQuestion = () => {
  if (currentQuestionIndex.value > 0) {
    saveCurrentAnswer();
    currentQuestionIndex.value--;
    loadAnswerForCurrentQuestion();
  }
};

const nextQuestion = () => {
  saveCurrentAnswer();
  if (isLastQuestion.value) {
    const answeredCount = Object.keys(answers.value).length;
    if (answeredCount < questions.value.length) {
      if (!window.confirm(`Anda belum menjawab semua soal (${answeredCount}/${questions.value.length}). Yakin ingin menyelesaikan tes?`)) {
        return;
      }
    }
    submitTest();
  } else {
    currentQuestionIndex.value++;
    loadAnswerForCurrentQuestion();
  }
};



// Di dalam <script setup> di TestTaker.vue

const submitTest = async () => {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  
  clearInterval(timerInterval.value);
  saveCurrentAnswer();
  
  const testId = route.params.id;
  const formattedAnswers = Object.values(answers.value);
  const payload = { test_id: parseInt(testId), answers: formattedAnswers };

  try {
    // --- PERUBAHAN UTAMA DI SINI ---
    // 1. Tangkap respons dari API
    const response = await apiClient.post('/sessions/submit-answers', payload);
    
    // 2. Ambil skor dari data respons
    const finalScore = response.data.score; 
    
    // 3. Hapus data sesi tes yang sudah tidak relevan
    localStorage.removeItem('test_end_time');
    
    // 4. Arahkan ke halaman ThankYou DAN kirim skor sebagai query parameter
    router.push({ name: 'ThankYou', query: { score: finalScore } });

  } catch (err) {
    alert('Gagal menyimpan jawaban. Silakan coba lagi.');
    console.error(err);
    isSubmitting.value = false;
  }
};

const leaveCount = ref(0); // Menghitung berapa kali user meninggalkan halaman
const showLeaveWarning = ref(false); // State untuk menampilkan modal peringatan

/**
 * Fungsi ini dipanggil ketika pengguna beralih tab, ke home screen,
 * atau melakukan aksi lain yang membuat halaman tes tidak lagi terlihat.
 */
const handleVisibilityChange = () => {
  // 'document.hidden' adalah cara modern untuk memeriksa visibilitas
  if (document.hidden) {
    leaveCount.value++;
    console.log(`Peringatan: Pengguna meninggalkan halaman. Jumlah: ${leaveCount.value}`);
    
    // Tampilkan modal peringatan
    showLeaveWarning.value = true;

    // Di sini Anda bisa menambahkan logika lain, misalnya:
    // - Mengirim log ke server bahwa pengguna ini melakukan aktivitas mencurigakan.
    // - Jika leaveCount.value > 3, secara otomatis menghentikan tes.
  }
};



// --- Lifecycle Hooks ---
onMounted(async () => {
  isLoading.value = true;
  error.value = '';
  const testId = route.params.id;
  
  try {
    const testResponse = await apiClient.get(`/tests/${testId}/preview`);
    
    testDetails.value = testResponse.data;
    await fetchQuestions(testId);

    if (questions.value.length > 0) {
      loadAnswerForCurrentQuestion();
    }

    if (testDetails.value.memorization_duration_seconds && testDetails.value.memorization_duration_seconds > 0) {
      stage.value = 'memorizing';
      startMemorizationTimer();
    } else {
      stage.value = 'answering';
      startAnsweringTimer();
    }
  } catch (err) {
    error.value = "Gagal memuat tes. Sesi mungkin tidak valid atau tes tidak ditemukan.";
    console.error(err);
  } finally {
    isLoading.value = false;
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onBeforeUnmount(() => {
  clearInterval(timerInterval.value);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<style scoped>
/* === LAYOUT UTAMA & CARD === */
.test-taker-page {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 100vh;
  background-color: #f0f2f5;
  padding: 20px 10px; /* Lebih ramah mobile */
}

.test-card {
  width: 100%;
  max-width: 800px;
  background-color: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.stage-content {
  padding: 20px;
}

/* === TIMER & LOADING === */
.timer-bar {
  background-color: #2c3e50;
  color: white;
  padding: 12px;
  text-align: center;
  font-size: 1.1em;
  font-weight: 500;
}
.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
  color: #555;
}
.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #1abc9c;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* === KONTEN HAFALAN === */
.memorization-stage { text-align: center; }
.instruction { color: #555; margin-bottom: 20px; line-height: 1.5; }
.stimulus-content { border: 1px solid #ddd; padding: 20px; margin-top: 20px; border-radius: 6px; }
.stimulus-text { white-space: pre-wrap; text-align: left; }

/* === GAMBAR RESPONSIF (KUNCI UTAMA) === */
.stimulus-content img,
.question-image-container img,
.option-image {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 10px auto;
  border-radius: 6px;
}

/* === NAVIGASI SOAL === */
.question-navigator {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-bottom: 20px;
}
.nav-box {
  width: 32px;
  height: 32px;
  line-height: 32px;
  font-size: 0.9em;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  background-color: #fff;
  transition: all 0.2s;
  font-weight: bold;
  flex-shrink: 0;
}
.nav-box:hover {
  border-color: #1abc9c;
}
.nav-box.answered {
  background-color: #d4edda;
  border-color: #c3e6cb;
}
.nav-box.active {
  background-color: #3498db;
  color: white;
  border-color: #2980b9;
}

/* === PROGRESS BAR & HEADER SOAL === */
.progress-bar-container {
  width: 100%;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 5px;
}
.progress-bar-fill {
  height: 100%;
  background-color: #1abc9c;
  border-radius: 4px;
  transition: width 0.3s ease-in-out;
}
.progress-text {
  text-align: right;
  font-size: 0.9em;
  color: #777;
  margin-bottom: 20px;
}
.test-header h1 {
  font-size: 1.5em;
  line-height: 1.4;
  margin: 0 0 20px 0;
}
.question-image-container { 
  margin-bottom: 20px; 
}

/* === AREA JAWABAN === */
.answer-area { margin-top: 30px; }
.answer-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.option {
  border: 2px solid #e0e0e0;
  padding: 15px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 15px;
}
.option:hover {
  border-color: #1abc9c;
}
.option.selected {
  background-color: #e8f8f5;
  border-color: #16a085;
  font-weight: 500;
}
.option-image {
  max-width: 120px; /* Batasi ukuran gambar di pilihan */
  max-height: 120px;
  flex-shrink: 0; /* Mencegah gambar menyusut */
}

/* Untuk checkbox */
.checkbox-option {
  flex-direction: row;
}
.checkbox-option input {
  width: 20px;
  height: 20px;
}

/* Untuk essay */
textarea {
  width: 100%;
  padding: 12px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1em;
  font-family: inherit;
  resize: vertical;
}
textarea:focus {
  outline: none;
  border-color: #1abc9c;
}

/* === TOMBOL NAVIGASI BAWAH === */
.navigation-buttons {
  display: flex;
  justify-content: space-between;
  margin-top: 30px;
  border-top: 1px solid #eee;
  padding-top: 20px;
  gap: 15px;
}
.navigation-buttons button {
  flex-grow: 1; /* Tombol meregang di mobile */
  padding: 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 1em;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background-color 0.2s, border-color 0.2s;
}
.button-primary {
  background-color: #2c3e50;
  color: white;
}
.button-primary:hover:not(:disabled) {
  background-color: #34495e;
}
.button-secondary {
  background-color: #ecf0f1;
  color: #2c3e50;
  border-color: #bdc3c7;
}
.button-secondary:hover:not(:disabled) {
  background-color: #e0e5e6;
}
.navigation-buttons button:disabled {
  background-color: #f0f0f0;
  color: #aaa;
  cursor: not-allowed;
  border-color: #e0e0e0;
}

/* === MEDIA QUERY UNTUK DESKTOP === */
@media (min-width: 768px) {
  .test-taker-page {
    padding: 40px 20px;
  }
  .stage-content {
    padding: 30px 40px;
  }
  .test-header h1 {
    font-size: 1.8em;
  }
  .navigation-buttons button {
    flex-grow: 0; /* Kembalikan ke ukuran normal di desktop */
    min-width: 150px;
  }
}

/* === GAYA UNTUK MODAL PERINGATAN === */
.warning-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000; /* Pastikan di atas semua elemen lain */
  padding: 20px;
}

.warning-modal {
  background: white;
  padding: 30px;
  border-radius: 8px;
  max-width: 450px;
  width: 100%;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
}

.warning-modal h2 {
  margin-top: 0;
  color: #f39c12; /* Warna kuning untuk peringatan */
}

.warning-modal p {
  line-height: 1.6;
  color: #333;
}

.warning-count {
  margin-top: 20px;
  padding: 10px;
  background-color: #fdf3e6;
  border: 1px solid #fbe6c9;
  border-radius: 6px;
}

/* Gunakan kembali gaya .button-primary yang sudah ada */
.warning-modal .button-primary {
  margin-top: 20px;
  width: auto; /* Jangan meregang penuh */
  min-width: 150px;
}
</style>